import { ArrowLeft, Check, FileCheck2, Send, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsCommentairesCloture, useCommentairesCloture } from '@/api/hooks';
import { LIBELLE_ALERTE_RAPPORT, LIBELLE_STATUT_COMMENTAIRE, type AttenteCommentaire, type ClotureResume } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr } from '@/lib/utils';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/** Une direction attendue : alertes, explication du directeur (saisie ou reprise), validation ou retour par le DF. */
function Attente({ a, periode }: { a: AttenteCommentaire; periode: ClotureResume['periode'] }) {
  const actions = useActionsCommentairesCloture();
  const c = a.commentaire;
  const [edition, setEdition] = useState(!c && a.peutSaisir);
  const [texte, setTexte] = useState(c?.texte ?? '');
  const [motif, setMotif] = useState('');
  const [retour, setRetour] = useState(false);
  const enregistrer = async () => {
    try {
      await actions.enregistrer.mutateAsync({ exercice: periode.exercice, type: periode.type, indice: periode.indice, structureCode: a.structureCode, serviceCode: a.serviceCode || undefined, texte });
      toast.success(`Explication de ${a.serviceCode ? `${a.structureCode}/${a.serviceCode}` : a.structureCode} transmise au Directeur financier.`);
      setEdition(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const valider = async () => {
    if (!c) return;
    try {
      await actions.valider.mutateAsync(c.id);
      toast.success(`Explication de ${a.structureCode} validée : elle sera reprise dans les rapports.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation impossible.');
    }
  };
  const retourner = async () => {
    if (!c) return;
    try {
      await actions.retourner.mutateAsync({ id: c.id, motif });
      toast.success(`Explication de ${a.structureCode} retournée au directeur.`);
      setRetour(false);
      setMotif('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retour impossible.');
    }
  };
  const codes = [...new Set(a.alertes.map((x) => x.code))];
  return (
    <Card className={cn(!c && a.enRetard && 'border-red-300')}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {a.serviceCode ? (
            <>
              <Badge variant="secondary">service</Badge> {a.structureCode}/{a.serviceCode} · {a.serviceLibelle}
            </>
          ) : (
            <>
              {a.structureCode} · {a.structureLibelle}
            </>
          )}
          {a.nombreLignesEnAlerte > 0 ? <Badge variant="destructive">{a.nombreLignesEnAlerte} ligne(s) en alerte</Badge> : <Badge variant="outline">sans alerte</Badge>}
          {c ? <Badge variant={c.statut === 'VALIDE' ? 'default' : c.statut === 'RETOURNE' ? 'destructive' : 'secondary'}>{LIBELLE_STATUT_COMMENTAIRE[c.statut]}</Badge> : <Badge variant={a.enRetard ? 'destructive' : 'outline'}>{a.enRetard ? 'non commenté · date limite dépassée' : `explication attendue avant le ${dateFr(a.dateLimite)}`}</Badge>}
        </CardTitle>
        {codes.length > 0 && <CardDescription>{codes.map((k) => LIBELLE_ALERTE_RAPPORT[k] ?? k).join(', ')}{a.alertes[0]?.detail ? ` — ${a.alertes[0].detail}` : ''}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-3">
        {c && !edition && (
          <div className="space-y-1">
            <p className="whitespace-pre-wrap text-sm">{c.texte}</p>
            <p className="text-xs text-muted-foreground">
              {c.auteurNom} · {dateHeureFr(c.saisiLe)}
              {c.statut === 'VALIDE' && c.valideParNom && ` · validé par ${c.valideParNom} le ${dateHeureFr(c.valideLe)}`}
            </p>
            {c.statut === 'RETOURNE' && c.motifRetour && <p className="text-sm text-red-700">Motif du retour : {c.motifRetour}</p>}
          </div>
        )}
        {edition && (
          <div className="space-y-2">
            <Label htmlFor={`texte-${a.structureCode}-${a.serviceCode}`}>{a.serviceCode ? 'Explication des écarts du service (reprise dans le rapport de la direction)' : 'Explication des écarts de la direction (reprise telle quelle dans les rapports)'}</Label>
            <Textarea id={`texte-${a.structureCode}-${a.serviceCode}`} rows={4} value={texte} onChange={(e) => setTexte(e.target.value)} maxLength={4000} placeholder="Causes des écarts, mesures prises, rattrapage attendu (lignes et montants)." />
            <div className="flex gap-2">
              <Button onClick={enregistrer} disabled={texte.trim().length < 10 || actions.enregistrer.isPending}>
                <Send className="size-4" /> {c ? 'Transmettre la reprise' : 'Transmettre au DF'}
              </Button>
              {c && (
                <Button variant="ghost" onClick={() => { setTexte(c.texte); setEdition(false); }}>
                  Annuler
                </Button>
              )}
            </div>
          </div>
        )}
        {!edition && a.peutSaisir && c && (
          <Button variant="outline" size="sm" onClick={() => setEdition(true)}>
            {c.statut === 'RETOURNE' ? "Reprendre l'explication" : 'Modifier'}
          </Button>
        )}
        {a.peutValider && c && (
          <div className="space-y-2 border-t pt-3">
            {retour ? (
              <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor={`motif-${a.structureCode}-${a.serviceCode}`}>Motif du retour</Label>
                  <Input id={`motif-${a.structureCode}-${a.serviceCode}`} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={500} placeholder="Préciser les lignes concernées et le montant attendu." />
                </div>
                <Button variant="destructive" onClick={retourner} disabled={motif.trim().length < 5 || actions.retourner.isPending}>
                  <Undo2 className="size-4" /> Retourner
                </Button>
                <Button variant="ghost" onClick={() => setRetour(false)}>
                  Annuler
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={valider} disabled={actions.valider.isPending}>
                  <Check className="size-4" /> Valider
                </Button>
                <Button variant="outline" onClick={() => setRetour(true)}>
                  <Undo2 className="size-4" /> Retourner au directeur
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Commentaires de clôture (cahier des charges du tableau de bord, écran E6) : explications des directions en écart, validées par le DF, reprises dans les rapports. */
export function CommentairesCloture() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const finalise = profils.some((p) => ['DIRECTEUR_FINANCIER', 'ADMINISTRATEUR'].includes(p));
  const [params, setParams] = useSearchParams();
  const maintenant = new Date();
  const precedent = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1, 1);
  const type = (params.get('type') === 'TRIMESTRE' ? 'TRIMESTRE' : params.get('type') === 'ANNEE' ? 'ANNEE' : 'MOIS') as 'MOIS' | 'TRIMESTRE' | 'ANNEE';
  const exercice = Number(params.get('exercice')) || precedent.getFullYear();
  const indice = type === 'ANNEE' ? 1 : Number(params.get('indice')) || (type === 'MOIS' ? precedent.getMonth() + 1 : Math.ceil((precedent.getMonth() + 1) / 3));
  const maj = (patch: Partial<{ exercice: number; type: string; indice: number }>) => setParams({ exercice: String(patch.exercice ?? exercice), type: patch.type ?? type, indice: String(patch.indice ?? (patch.type && patch.type !== type ? 1 : indice)) });
  const { data, isLoading, error } = useCommentairesCloture({ exercice, type, indice });
  const actions = useActionsCommentairesCloture();
  const finaliser = async () => {
    try {
      if (type === 'MOIS') {
        const r = await actions.finaliserMensuel.mutateAsync();
        toast.success(`Rapport définitif ${r.numero} produit${r.nombreEnvois ? ' et envoyé' : ''} ; il remplace le brouillon dans la bibliothèque.`);
      } else if (type === 'TRIMESTRE') {
        const r = await actions.finaliserTrimestriel.mutateAsync();
        toast.success(`Rapport trimestriel ${r.numero} produit : il attend votre visa dans la bibliothèque des rapports.`);
      } else {
        const r = await actions.finaliserAnnuel.mutateAsync(exercice);
        toast.success(`Rapport annuel ${r.numero} produit : il attend votre visa, puis la validation du Directeur Général.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Finalisation impossible.');
    }
  };
  const projet = async () => {
    try {
      const r = await actions.projetAnnuel.mutateAsync(exercice);
      toast.success(`Projet de rapport annuel ${r.numero} produit (brouillon).`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Production impossible.');
    }
  };
  const finalisationEnCours = actions.finaliserMensuel.isPending || actions.finaliserTrimestriel.isPending || actions.finaliserAnnuel.isPending;
  const attentes = data?.attentes ?? [];
  const commentees = attentes.filter((a) => a.commentaire).length;
  const validees = attentes.filter((a) => a.commentaire?.statut === 'VALIDE').length;
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/rapports" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Rapports d'exécution
          </Link>
        }
        titre="Commentaires de clôture"
        sousTitre="Pour chaque direction en écart au-delà des seuils, explication du directeur avant la date limite, validée par le Directeur financier et reprise automatiquement dans les rapports de la direction et de la Direction Générale."
        actions={
          <>
            <SelectNatif value={type} onChange={(e) => maj({ type: e.target.value })} aria-label="Type de période">
              <option value="MOIS">Mois</option>
              <option value="TRIMESTRE">Trimestre</option>
              <option value="ANNEE">Exercice (rapport annuel)</option>
            </SelectNatif>
            {type !== 'ANNEE' && (
              <SelectNatif value={indice} onChange={(e) => maj({ indice: Number(e.target.value) })} aria-label="Période">
                {type === 'MOIS'
                  ? MOIS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))
                  : [1, 2, 3, 4].map((t) => (
                      <option key={t} value={t}>
                        T{t}
                      </option>
                    ))}
              </SelectNatif>
            )}
            <Input type="number" className="num w-24" min={2020} max={2100} value={exercice} onChange={(e) => maj({ exercice: Number(e.target.value) })} aria-label="Exercice" />
          </>
        }
      />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Période" valeur={data.periode.libelle} detail={`du ${dateFr(data.periode.debut)} au ${dateFr(data.periode.fin)}`} />
            <Kpi label="Date limite des explications" valeur={dateFr(data.periode.dateLimite)} detail={`directeurs · chefs de service avant le ${dateFr(data.dateLimiteServices)}${data.periode.enRetard ? ' · dépassée' : ''}`} accent={data.periode.enRetard && commentees < attentes.length ? 'critique' : 'ok'} />
            <Kpi label="Directions et services attendus" valeur={attentes.length} detail={`${attentes.filter((a) => !a.serviceCode).length} direction(s), ${attentes.filter((a) => a.serviceCode).length} service(s)`} />
            <Kpi label="Explications" valeur={`${commentees} / ${attentes.length}`} detail={`${validees} validée(s) par le DF`} accent={attentes.length && commentees === attentes.length ? 'ok' : commentees ? 'alerte' : undefined} />
          </div>
          {finalise && (
            <Card>
              <CardHeader>
                <CardTitle>{type === 'MOIS' ? 'Rapport mensuel de la Direction Générale' : type === 'TRIMESTRE' ? 'Rapport trimestriel de la Direction Générale' : `Rapport annuel du Fonds · exercice ${exercice}`}</CardTitle>
                <CardDescription>
                  {type === 'MOIS'
                    ? 'Le brouillon est produit le 3e jour ouvré du mois suivant avec les rapports de service et de direction ; la version définitive, qui reprend les explications validées (ou la mention « non commenté »), est produite automatiquement le lendemain de la date limite (J+5 ouvrés). Vous pouvez la produire dès maintenant.'
                    : type === 'TRIMESTRE'
                      ? 'Le brouillon est produit le 5e jour ouvré après le trimestre avec les rapports de direction ; après la date limite des explications, une version à viser est produite automatiquement. Votre visa, dans la bibliothèque, la rend définitive et l’envoie au DG, aux DGA et à tous les directeurs. Vous pouvez produire la version à viser dès maintenant.'
                      : 'Les rapports annuels des directions partent le 15 janvier, le projet du rapport annuel du Fonds est produit le 20 janvier ; après la date limite des explications, la version à viser est produite. Votre visa puis la validation du Directeur Général la rendent définitive ; sa diffusion (Conseil d’administration, tutelle, partenaires, commissaire aux comptes) se déclenche ensuite depuis la bibliothèque.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {type === 'ANNEE' && (
                  <Button variant="outline" onClick={projet} disabled={actions.projetAnnuel.isPending}>
                    <FileCheck2 className="size-4" /> {actions.projetAnnuel.isPending ? 'Production…' : 'Produire le projet du rapport annuel'}
                  </Button>
                )}
                <Button variant="outline" onClick={finaliser} disabled={finalisationEnCours}>
                  <FileCheck2 className="size-4" /> {finalisationEnCours ? 'Production…' : type === 'MOIS' ? 'Produire la version définitive du mois écoulé' : type === 'TRIMESTRE' ? 'Produire la version à viser du trimestre écoulé' : 'Produire la version à viser du rapport annuel'}
                </Button>
              </CardContent>
            </Card>
          )}
          {attentes.length === 0 ? (
            <Vide message="Aucune direction en écart sur cette période dans votre périmètre." />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {attentes.map((a) => (
                <Attente key={`${a.structureCode}-${a.serviceCode}-${a.commentaire?.updatedAt ?? ''}`} a={a} periode={data.periode} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
